import { Col } from 'antd'
import React, { useEffect, useMemo, useRef, useState } from 'react'

import { useWindowResize } from '@/hooks/useWindowResize/index'
import { ORDER_VERTICAL } from '@/pages/TowerQianKun/ChannelOrder/components/Page/constants'

/** 查询容器布局 */

/** 屏幕宽度对应不同的短列，必须是偶数 */
function getColNumPerLine(queryWidth: number) {
    if (queryWidth >= 2398) {
        return 14
    } else if (queryWidth >= 2070) {
        return 12
    } else if (queryWidth >= 1742) {
        return 10
    } else if (queryWidth >= 1414) {
        return 8
    } else {
        return 6
    }
}

export const Layout = {
    ROW: 'ROW', // 独占一行
    GROUP: 'GROUP', // 大列:宽度是两个 ITEM
    ITEM: 'ITEM' // 小列, 默认
}

interface LayoutField {
    /** 抽象占位 */
    layout?: number
    /** 组件当前是否应该折叠 */
    _collapsed?: boolean
    /** 组件当前所在行数 */
    _rowIndex?: number
    /** 当 false 时，通过display:none隐藏 */
    show?: boolean
}

interface LayoutConfig {
    /** 指定收起行数 */
    collapsedNum?: number
    /** 默认折叠状态 */
    defaultCollapsed?: boolean
    /** 默认extend样式 */
    defaultExtendStyle?: any
    /** 固定宽度 */
    queryWidth?: number
}

interface LayoutEvent {
    /** 一行展示几项 */
    colNumPerLine: number
    /** 总行数 */
    rowNum: number
    /** 全部的fields */
    allFields: any[]
    /** 显示的fields */
    fields: any[]
    /** 扩展项 */
    extendArea: any
    /** 切换折叠 */
    toggleCollapse: () => void
    /** 折叠状态 */
    collapsed: boolean
    /** 是否展示折叠 */
    collapseVisible: boolean
}

export function useQueryLayout(initialFields: LayoutField, config: LayoutConfig = {}): LayoutEvent {
    const $this = useRef({
        config: config || {}
    })
    const [collapsed, setCollapsed] = useState(config.defaultCollapsed)
    const windowSize = useWindowResize()

    const toggleCollapse = () => {
        setCollapsed(prevState => !prevState)
    }

    const colNumPerLine = useMemo(() => {
        if (config?.direction === 'vertical') {
            return
        }

        const queryWidth = config?.queryWidth || windowSize.width

        return getColNumPerLine(queryWidth)
    }, [config?.queryWidth, windowSize?.width, config?.direction])

    const { showFields, hideFields } = useMemo(() => {
        return {
            showFields: getShowFields(initialFields),
            hideFields: getHideFields(initialFields)
        }
    }, [initialFields])

    // 给 show field 打上 layout
    const layoutFields = useMemo(() => {
        return calcFieldsLayout(showFields, colNumPerLine)
    }, [showFields, colNumPerLine])

    const colNums = useMemo(() => {
        return calcColNumLine(layoutFields, colNumPerLine)
    }, [layoutFields, colNumPerLine])

    const rowNum = Math.ceil(colNums / colNumPerLine)

    // 给 show field 打上 _collapsed
    const layoutCollapsedFields = useMemo(() => {
        const { collapsedNum } = $this.current.config
        return layoutFields.map(f => {
            f._collapsed = collapsed && typeof collapsedNum === 'number' && f._rowIndex >= collapsedNum
            return f
        })
    }, [layoutFields, collapsed])

    const showColNums = useMemo(() => {
        const showFields = layoutCollapsedFields.filter(_ => !_._collapsed)
        return calcColNumLine(showFields, colNumPerLine)
    }, [layoutCollapsedFields, colNumPerLine])

    const extendArea = useMemo(() => {
        return getExtendArea(showColNums, colNumPerLine, $this.current.config?.defaultExtendStyle)
    }, [showColNums, colNumPerLine])

    const collapseVisible = useMemo(() => {
        const { collapsedNum } = $this.current.config
        return typeof collapsedNum === 'number' ? rowNum > collapsedNum : false
    }, [rowNum])

    // 重置
    useEffect(() => {
        if (!collapseVisible) {
            setCollapsed($this.current.config.defaultCollapsed)
        }
    }, [collapseVisible])

    return {
        colNumPerLine,
        rowNum,
        allFields: layoutFields,
        fields: layoutCollapsedFields.concat(hideFields),
        extendArea,
        toggleCollapse,
        collapsed,
        collapseVisible
    }
}

function calcFieldsLayout(initialFields, colNumPerLine: number) {
    let fields = [...initialFields]
    fields.forEach(f => {
        if (!f.layout) {
            f.layout = f.group ? Layout.GROUP : Layout.ITEM
        }
    })
    const maxIndex = fields.length - 1
    let rowIndex = 0
    let fieldsPerLine = []
    fields.forEach((f, i) => {
        let currColNum = calcColNumLine(fieldsPerLine, colNumPerLine)
        if (currColNum === colNumPerLine) {
            const nextLineCols = adjustMoveToNext(fieldsPerLine, colNumPerLine)
            rowIndex++
            updateRowIndex(nextLineCols, rowIndex)
            fieldsPerLine = [...nextLineCols, f]
        } else if (currColNum + getColNum(f.layout, colNumPerLine) > colNumPerLine) {
            let lastField = findLastLayoutItem(fieldsPerLine)
            lastField.layout = Layout.GROUP
            fieldsPerLine = [f]
            rowIndex++
        } else {
            fieldsPerLine.push(f)
        }
        // 最后一个控件，需要补齐当前大列
        if (i === maxIndex) {
            const nextLineCols = adjustMoveToNext(fieldsPerLine, colNumPerLine)
            if (nextLineCols?.length) {
                rowIndex++
                updateRowIndex(nextLineCols, rowIndex)
            }
            currColNum = calcColNumLine(fieldsPerLine, colNumPerLine)
            if (currColNum % 2 !== 0) {
                f.layout = Layout.GROUP
            }
        }
        f._rowIndex = rowIndex
    })
    return fields
}

function updateRowIndex(list, rowIndex) {
    if (list?.length) {
        list.forEach(_ => (_._rowIndex = rowIndex))
    }
}

/** 找到第一个 ITEM 并展开，然后重新计算剩余的 */
function adjustMoveToNext(fieldsPerLine, colNumPerLine) {
    let moveToNext = []
    let currNum = 0
    for (let i = 0; i < fieldsPerLine.length; i++) {
        if (
            i < fieldsPerLine.length - 1 &&
            currNum % 2 === 0 &&
            fieldsPerLine[i].layout === Layout.ITEM &&
            fieldsPerLine[i + 1].layout !== Layout.ITEM
        ) {
            fieldsPerLine[i].layout = Layout.GROUP
        }

        currNum += calcColNumLine([fieldsPerLine[i]], colNumPerLine)

        if (currNum > colNumPerLine) {
            moveToNext = fieldsPerLine.slice(i)
            break
        }
    }
    return moveToNext
}

function getColNum(layout: string, colNumPerLine: number) {
    if (layout === Layout.ROW) return colNumPerLine
    return layout === Layout.ITEM ? 1 : 2
}

function calcColNumLine(lineFields, colNumPerLine: number) {
    return lineFields.reduce((total, currentValue, currentIndex, arr) => {
        return (
            total + (currentValue.layout === Layout.ROW ? colNumPerLine : currentValue.layout === Layout.GROUP ? 2 : 1)
        )
    }, 0)
}

/**
 * 从后往前找到第一个 Layout.ITEM
 */
function findLastLayoutItem(lineFields) {
    let found
    for (let i = lineFields.length - 1; i >= 0; i--) {
        if (lineFields[i].layout === Layout.ITEM) {
            found = lineFields[i]
            break
        }
    }
    return found
}

/**
 * 这里的 show 要前置处理,这里只是兜底
 * display: !field.show ? 'none' : undefined
 * */
export function LayoutWrap({ field, children, colNumPerLine, extendArea, querySearchType }) {
    if (extendArea) {
        return <Col {...extendArea}>{children}</Col>
    }

    if (querySearchType === ORDER_VERTICAL) {
        return (
            <span
                style={{
                    display: field._collapsed || field.show === false ? 'none' : undefined
                }}
            >
                {children}
            </span>
        )
    }

    // 兼容
    if (!colNumPerLine) {
        return children
    }
    const width = calcColWith(field?.layout, colNumPerLine)

    return (
        <Col
            style={{
                width: `${width}%`,
                display: field?._collapsed || field?.show === false ? 'none' : undefined
            }}
        >
            {children}
        </Col>
    )
}

export function calcColWith(layout, colNumPerLine) {
    const item = 100 / colNumPerLine
    if (layout === Layout.ROW) {
        return 100
    } else if (layout === Layout.GROUP) {
        return item * 2
    } else {
        return item
    }
}

function getExtendArea(colNums: number, colNumPerLine: number, defaultExtendStyle) {
    const item = 100 / colNumPerLine
    const rest = colNumPerLine - (colNums % colNumPerLine)
    // 默认布局样式
    return {
        style: defaultExtendStyle ?? {
            width: `${item * rest}%`,
            textAlign: 'right',
            display: 'flex',
            justifyContent: 'flex-end'
        }
    }
}

function getShowFields(fields) {
    // 兼容没有 show 的场景
    return fields.filter(field => field.show === true || field.show === undefined)
}

function getHideFields(fields) {
    return fields.filter(field => field.show === false)
}
